<template>
  <PortalLayout section="Prepare" hidePortalSelector hideHeader>
    <div class="pb-4">
      <BackButton :to="'../trainingmodule/' + trainingTest.moduleId">
        Back to Training Module
      </BackButton>
    </div>
    <div v-if="isPropertyLoading || isTestLoading"><Loading /></div>
    <div v-else>
      <h1 class="py-3 mb-6 border-b flex">
        <div class="text-xl flex-grow">
          <div class="font-bold">Edit Test</div>
          <div>
            {{ trainingTest?.title }}
            <span v-if="!trainingTest?.isActive">(inactive)</span>
          </div>
        </div>
        <EditButton @click="editTestClick">Edit</EditButton>
      </h1>
      <h2 class="font-medium">Questions</h2>
      <div v-if="testQuestions && testQuestions.length > 0">
        <AddButton class="mt-2" @click="addQuestionClick">Add Question</AddButton>
        <div
          v-for="question in testQuestions"
          :key="question.questionId"
          class="p-4 my-2 border-t bg-gray-100"
        >
          <div class="flex">
            <div
              class="font-medium py-1 px-2 flex-grow rounded cursor-pointer hover:bg-blue-200"
              @click="questionClick(question)"
            >
              {{ question.text }}
            </div>
            <EditButton @click="editQuestionClick(question)" iconOnly="true" />
          </div>
          <div class="px-2">{{ question.explanation }}</div>
          <div v-if="question.answers.length" class="px-2">
            <br />
            Answers
            <li
              v-for="answer in question.answers"
              :key="answer.answerId"
              class="rounded disc pl-4"
              :class="answer.isCorrect ? 'bg-green-200' : ''"
            >
              <span class="text-sm">
                {{ answer.text }} <span v-if="answer.isCorrect">(correct)</span>
              </span>
            </li>
          </div>
        </div>
      </div>
      <div class="m-2" v-else>
        <span>-- No Questions found for this Test --</span>
      </div>
      <AddButton @click="addQuestionClick">Add Question</AddButton>
      <EditPopup
        entity-name="Question"
        :is-visible="isQuestionPopupVisible"
        :is-edit-mode="isQuestionEditing"
        :save-error="saveError"
        :delete-enabled="true"
        @close="isQuestionPopupVisible = false"
        @save="saveQuestionClick"
        @delete="deleteQuestionClick"
      >
        <BaseInput
          label="Text"
          v-model="questionText"
          :error="errorsQuestion.questionText"
        />
        <div class="h-3" />
        <BaseInput
          label="Explanation"
          v-model="questionExplanation"
          :error="errorsQuestion.questionExplanation"
        />
      </EditPopup>
      <EditPopup
        entity-name="Test"
        :is-visible="isTestPopupVisible"
        :is-edit-mode="true"
        :save-error="saveError"
        :delete-enabled="false"
        @close="isTestPopupVisible = false"
        @save="saveTestClick"
      >
        <BaseInput label="Title" v-model="testTitle" :error="errorsTest.testTitle" />
        <div class="h-3" />
        <BaseCheckbox label="Currently Active" v-model="testIsActive" />
      </EditPopup>
    </div>
  </PortalLayout>
</template>

<script setup lang="ts">
import { useBldgStore } from "../../store/bldgStore";
import { TestDTO, QuestionDTO } from "../../store/generatedFetchClient";
import { useTrainingStore } from "../../store/trainingStore";

import { useField, useForm } from "vee-validate";
import * as yup from "yup";

const bldgStore = useBldgStore();
const trainingStore = useTrainingStore();
const route = useRoute();
const router = useRouter();

var isPropertyLoading = computed(() => bldgStore.isLoading);
var isTestLoading = ref(true);
var property = computed(() => bldgStore.property);

let trainingTest = computed(() => trainingStore.selectedTest);
let testQuestions = computed(() => trainingStore.selectedTestQuestions);

let questionClick = function (question: QuestionDTO) {
  router.push(`../question/${question.questionId}`);
};

onMounted(loadData);
watch(property, loadData);
async function loadData() {
  isTestLoading.value = true;
  if (property.value?.propertyId) {
    if (route.params.testId && typeof route.params.testId === "string") {
      let testId = Number.parseInt(route.params.testId);
      await trainingStore.getTest(testId);
      await trainingStore.getQuestionsByTest(testId);
    }
  }
  isTestLoading.value = false;
}

// Popup stuff
let saveError = ref("");

function onInvalidSubmit(ctx: any) {
  saveError.value = "One or more invalid form inputs.";
  console.log("error trying to submit form", ctx);
}

// Test Edit
let isTestPopupVisible = ref(false);

const { handleSubmit: handleSubmitTest, errors: errorsTest } = useForm({
  validationSchema: yup.object({
    testTitle: yup.string().required().max(50).label("Name"),
  }),
  initialValues: {
    testTitle: "",
  },
});

const { value: testTitle } = useField<string>("testTitle");
let testIsActive = ref(true);

let saveTestClick = handleSubmitTest(onValidSubmitTest, onInvalidSubmit);

async function onValidSubmitTest() {
  let newTest = {
    testId: trainingTest.value.testId,
    moduleId: trainingTest.value.moduleId,
    title: testTitle.value,
    isActive: testIsActive.value,
  } as TestDTO;

  saveError.value = "";

  if (property.value && property.value.portalId) {
    saveError.value = await trainingStore.saveTest(newTest);
    if (!saveError.value) {
      trainingTest.value.title = testTitle.value;
      trainingTest.value.isActive = testIsActive.value;
      isTestPopupVisible.value = false;
    }
  }
}

// Question Edit
let isQuestionPopupVisible = ref(false);
let isQuestionEditing = ref(false);

const { handleSubmit: handleSubmitQuestion, errors: errorsQuestion } = useForm({
  validationSchema: yup.object({
    questionText: yup.string().required().max(256).label("Text"),
    questionExplanation: yup.string().required().max(1024).label("Explanation"),
  }),
  initialValues: {
    questionText: "",
    questionExplanation: "",
  },
});

let questionId = ref(0);
const { value: questionText } = useField<string>("questionText");
const { value: questionExplanation } = useField<string>("questionExplanation");

let saveQuestionClick = handleSubmitQuestion(onValidSubmitQuestion, onInvalidSubmit);

async function onValidSubmitQuestion() {
  let newQuestion = {
    questionId: questionId.value,
    testId: trainingTest.value.testId,
    text: questionText.value,
    explanation: questionExplanation.value,
  } as QuestionDTO;

  saveError.value = "";

  if (property.value && property.value.portalId) {
    saveError.value = await trainingStore.saveQuestion(newQuestion);
    if (!saveError.value) {
      await trainingStore.getQuestionsByTest(trainingTest.value.testId);
      isQuestionPopupVisible.value = false;
    }
  }
}

let editTestClick = function () {
  testTitle.value = trainingTest.value.title;
  testIsActive.value = trainingTest.value.isActive;

  saveError.value = "";

  isTestPopupVisible.value = true;
};

let addQuestionClick = function () {
  questionId.value = 0;
  questionText.value = "";
  questionExplanation.value = "";

  saveError.value = "";

  isQuestionEditing.value = false;
  isQuestionPopupVisible.value = true;
};

let editQuestionClick = function (selectedQuestion: QuestionDTO) {
  questionId.value = selectedQuestion.questionId ?? 0;
  questionText.value = selectedQuestion.text ?? "";
  questionExplanation.value = selectedQuestion.explanation ?? "";

  saveError.value = "";

  isQuestionEditing.value = true;
  isQuestionPopupVisible.value = true;
};

async function deleteQuestionClick() {
  if (questionId.value) {
    console.log(`Deleting Question ${questionId.value}`);
    saveError.value = await trainingStore.deleteQuestion(questionId.value);
    if (!saveError.value) {
      await trainingStore.getQuestionsByTest(trainingTest.value.testId);
      isQuestionPopupVisible.value = false;
    }
  }
}
</script>
